import { i18n } from "@verbleif/lib/src/translations/i18n";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { refreshEffectiveUserPreferences } from "@verbleif/shared/src/composables/Users/UserPreferences/refreshEffectiveUserPreferences";
import { until } from "@vueuse/core";
import { computed } from "vue";
import { toast } from "vue-sonner";

export const BULK_ASSIGN_ROUTE_NAME = "tasks-bulk-assign";

/**
 * The effective preferences of the current user. On a cold reload the route resolves before the session is
 * loaded: wait for the user (the session refresh sends a failed login to the login page on its own), then use the
 * preferences the session loaded, or load them when they are not there yet.
 */
async function loadPreferences() {
  const { user, userPreferences } = useAuthStore();
  if (userPreferences.value !== null) {
    return userPreferences.value;
  }
  await until(computed(() => user.value !== null)).toBe(true);
  if (userPreferences.value !== null) {
    return userPreferences.value;
  }
  return refreshEffectiveUserPreferences();
}

/**
 * The page is only for people who switched on bulk assigning in their preferences; everyone else goes to the
 * task list. When the preferences cannot be loaded the user also goes to the task list, with an error that says
 * why, so a direct link never ends on an empty page.
 */
export async function bulkAssignBeforeEnter(): Promise<true | { path: string }> {
  let preferences;
  try {
    preferences = await loadPreferences();
  } catch (error) {
    console.error(error);
    toast.error(i18n.global.t("bulk_task_assignee.preferences_failed"));
    return { path: "/tasks" };
  }

  if (preferences?.enableBulkTaskAssignee === true) {
    return true;
  }

  return { path: "/tasks" };
}
